import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Globe, Zap, Database, DollarSign, Clock, Calendar, Percent, Info } from 'lucide-react';
import { usageService, presetToDateRange } from '../services/usageService';
import apiService from '../services/apiService';
import { useLanguage } from '../i18n';
import { userErrorMessage } from '../utils/userError';
import TelemetrySection from './TelemetrySection';

const STEP_LABELS = {
    // Agentic RAG pipeline steps (tool-based)
    search_documents: 'Search Docs',
    search_within_document: 'Search In Doc',
    read_document: 'Read Document',
    list_documents: 'List Documents',
    move_document: 'Move Document',
    research: 'Research (Subagent Call)',
    research_subagent: 'Research Subagent',
    rulebook_scribe: 'Rulebook Scribe',
    get_rulebook: 'Read Rulebook',
    edit_rulebook: 'Edit Rulebook',
    context_cache: 'Prompt Cache (write + storage)',
    light_chat: 'PAI Conversations (Fast Track)',
    edit_document: 'Edit Document',
    create_document: 'Create Document',
    document_author: 'Doc Author (PDF HTML)',
    doc_code_worker: 'Doc Code Worker',
    web_search: 'Web Search',
    calculate: 'Calculate',
    get_energy_status: 'Energy Status',
    web_search_grounded: 'Web Search (Grounded)',
    read_link: 'Read Link',
    read_link_url_context: 'Read Link (URL Context)',
    places_lookup: 'Google Places Lookup',
    agentic_response: 'Agentic Response',
    agentic_final_response: 'Agentic Final',
    conversation_summary: 'Conv. Summary',
    conversation_title: 'Conv. Title',
    // Still used by active pipelines
    doc_identifier: 'Doc Identifier',
    edit_response: 'Edit Response (DOCX)',
    learning: 'Learning',
    ocr: 'OCR (Vision)',
    audio_transcription: 'Audio Transcription',
    embedding_index: 'Embedding (Index)',
    embedding_query: 'Embedding (Query)',
    create_calendar_event: 'Calendar Event',
    create_calendar_events: 'Calendar Events',
    resolve_calendar_package: 'Calendar Package',
    list_calendar_events: 'Read Calendar',
    remove_event_invitation: 'Remove Guest',
    create_reminder: 'Reminder',
    list_reminders: 'Read Reminders',
    update_reminder: 'Update Reminder',
    delete_reminders: 'Delete Reminders',
    // Notes engine ("external brain") + collaborative sharing (#259).
    create_note: 'Save Note',
    list_notes: 'Read Notes',
    update_note: 'Update Note',
    delete_notes: 'Delete Notes',
    share_note: 'Share Note',
    create_tasks: 'Add Tasks',
    list_tasks: 'Read Task List',
    update_tasks: 'Update Tasks',
    delete_tasks: 'Delete Tasks',
    create_expense: 'Log Expense',
    create_expense_batch: 'Log Receipts (Batch Dedup)',
    create_expenses_from_image: 'Log Receipts (Multi-per-image)',
    rename_book: 'Rename Book',
    assign_expense_to_book: 'Move Expense',
    list_expenses: 'Read Expenses',
    get_expense: 'Read Transaction',
    export_financial_data: 'Export Finances',
    import_transactions_from_spreadsheet: 'Import Spreadsheet',
    match_documents_to_expenses: 'Match Documents to Expenses',
    update_expense_categories: 'Update Expense Categories',
    update_expense: 'Update Expense',
    spreadsheet_import_mapping: 'Spreadsheet Column Mapping',
    search_transaction_items: 'Search Line Items',
    expense_extraction: 'Expense Extraction',
    list_boards: 'List Boards',
    get_board: 'Read Board',
    create_board: 'Create Board',
    rename_column: 'Rename Column',
    create_ticket: 'Create Ticket',
    update_ticket: 'Update Ticket',
    update_tickets: 'Batch Update Tickets',
    reorder_ticket: 'Reorder Ticket',
    delete_ticket: 'Delete Ticket',
    delete_tickets: 'Batch Delete Tickets',
    add_ticket_comment: 'Add Ticket Comment',
    list_ticket_comments: 'Read Ticket Comments',
    delete_ticket_comments: 'Delete Ticket Comments',
    attach_document_to_ticket: 'Attach to Ticket',
    detach_documents_from_ticket: 'Detach from Ticket',
    search_tickets: 'Search Tickets',
    // Teams (Agenda TO Contacts — Phase C / T5)
    list_teams: 'List Teams',
    get_team: 'Read Team',
    create_team: 'Create Team',
    add_team_members: 'Add Team Members',
    remove_team_member: 'Remove Team Member',
    leave_team: 'Leave Team',
    delete_team: 'Delete Team',
    share_document_to_team: 'Share Doc to Team',
    share_book_to_team: 'Share Book to Team',
    create_team_event: 'Create Team Event',
    // PAI-to-PAI direct send + Internal Secretary (Agenda TO Contacts Phase D / T6 + T4)
    send_document_to_contact: 'Send Document',
    send_contact_card: 'Send Contact Card',
    respond_to_event_invitation: 'Respond to Invitation',
    // Action ledger / undo (Double tap W/T #210)
    list_recent_actions: 'Recent Actions',
    revoke_action: 'Undo Action',
};

const formatTokens = (n) => {
    if (n == null) return '-';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
};

const formatCost = (usd) => {
    if (usd == null) return '-';
    if (usd < 0.01) return `$${usd.toFixed(4)}`;
    return `$${usd.toFixed(2)}`;
};

// Prompt-cache hit rate: prefer the BE-supplied cache_hit_pct, else derive it
// client-side from cached/prompt tokens (0/None-safe) so older backends still
// render a value. Cached input bills at ~1/10th of uncached — a drop here is a
// silent input-cost regression.
const cacheHitPct = (pct, cached, prompt) => {
    if (pct != null) return pct;
    const p = prompt || 0;
    if (p <= 0) return 0;
    return Math.round((100 * (cached || 0) / p) * 10) / 10;
};

const formatPct = (v) => (v == null ? '-' : `${Number(v).toFixed(1)}%`);

// PAI stat tile: alpha-white card, eyebrow label, 26px/800 tabular figure.
// The accent only tints the icon (DESIGN_SYSTEM.md accent rule).
const StatCard = ({ icon: Icon, label, value, sub, color = 'text-pai-blue' }) => (
    <div className="pai-card px-5 py-4 min-w-0">
        <div className="flex items-center gap-2 mb-2 min-w-0">
            <Icon size={15} className={`flex-shrink-0 ${color}`} />
            <span className="pai-eyebrow truncate">{label}</span>
        </div>
        <div className="text-[26px] font-extrabold tracking-[-0.025em] text-white tnum truncate" translate="no">{value}</div>
        {sub && <div className="text-[12.5px] text-pai-meta mt-1 tnum truncate" translate="no">{sub}</div>}
    </div>
);

// Segmented-control segment (same idiom as the Energy range picker).
const segClass = (on) => `px-2.5 py-1.5 text-[13px] font-semibold rounded-seg transition-colors ${
    on ? 'bg-white/[0.1] text-white' : 'text-pai-meta hover:text-white hover:bg-white/[0.06]'
}`;

const CARD_TITLE = 'text-[17px] font-bold text-white mb-3 flex items-center gap-2';
const TH = 'py-2 text-[11.5px] font-semibold uppercase tracking-[0.11em] text-pai-meta';

const PRESETS = [
    { days: 1, label: '24h' },
    { days: 7, label: '7d' },
    { days: 30, label: '30d' },
    { days: 90, label: '90d' },
];

const DashboardPage = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [tab, setTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Date range state
    const [startDate, setStartDate] = useState(() => presetToDateRange(30).startDate);
    const [endDate, setEndDate] = useState(() => presetToDateRange(30).endDate);
    const [activePreset, setActivePreset] = useState(30);
    const [billingCycle, setBillingCycle] = useState(false);
    const [showCustomRange, setShowCustomRange] = useState(false);
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');

    // Data. NO conversations / documents / per-turn drilldown: those endpoints
    // served titles, message text, raw LLM I/O and filenames, and were deleted
    // with the monitoring lockdown (2026-10-01). This page shows credit
    // statistics only — and since #470 (2026-10-16) ACCOUNT-WIDE only: the
    // Users tab, the per-account selector and the energy ± Adjust lever were
    // removed with GET /api/usage/users and POST …/energy/adjust. Energy grants
    // are the local BE script `scripts/adjust_energy.py`. ⚠️ Don't add a
    // per-account view back.
    const [summary, setSummary] = useState(null);
    // The dashboard is OPERATOR-ONLY (PO decision 2026-10-01): only the two
    // founder accounts may read it, keyed on an email allowlist server-side.
    //
    // ⚠️ Gate on `monitoring_access` from GET /auth/profile, NEVER on `role`.
    // The allowlist happens to name the same accounts the admin role does
    // today, but the role is not the rule — a third admin would inherit the
    // compute bill. Advisory anyway: the BE 404s every /api/usage/* call from
    // anyone else, which is the real gate.
    useEffect(() => {
        apiService.getUserProfile()
            .then((p) => {
                if (p?.monitoring_access !== true) navigate('/');
            })
            .catch(() => {});
    }, [navigate]);

    const applyPreset = useCallback((presetDays) => {
        const { startDate: s, endDate: e } = presetToDateRange(presetDays);
        setStartDate(s);
        setEndDate(e);
        setActivePreset(presetDays);
        setShowCustomRange(false);
    }, []);

    const applyCustomRange = useCallback(() => {
        if (!customStart || !customEnd) return;
        // endDate is exclusive — add 1 day so the selected end date is included
        const end = new Date(customEnd);
        end.setDate(end.getDate() + 1);
        setStartDate(customStart);
        setEndDate(end.toISOString().slice(0, 10));
        setActivePreset(null);
    }, [customStart, customEnd]);

    const toggleBillingCycle = useCallback(() => {
        setBillingCycle(prev => !prev);
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const dateParams = { startDate, endDate, billingCycle };
            setSummary(await usageService.getSummary(dateParams));
        } catch (err) {
            setError(userErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, billingCycle]);

    useEffect(() => { loadData(); }, [loadData]);

    // Overview only. The Conversations and Documents tabs went with the
    // endpoints behind them (monitoring lockdown, 2026-10-01); the Users tab
    // with the per-account listing (#470, 2026-10-16).
    const tabs = [
        { id: 'overview', label: t('auxTabOverview', 'Overview'), icon: Zap },
    ];

    return (
        // Full-page route outside WorkspaceShell, so it paints the same
        // wallpaper + scrim itself (absolute layers — never a fixed scrim).
        <div className="relative h-dvh overflow-hidden bg-pai-void text-white">
            <div aria-hidden="true" className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/wallpaper.jpg')" }} />
            <div aria-hidden="true" className="absolute inset-0 bg-[rgba(5,7,10,0.64)]" />
            <div className="relative h-full p-2 md:p-[14px]">
                <div className="pai-panel h-full flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="px-4 md:px-7 pt-5 pb-4 border-b border-pai-hairline">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <button
                                    onClick={() => navigate('/')}
                                    aria-label={t('commonBack', 'Back')}
                                    className="pai-icon-btn w-9 h-9 flex-shrink-0"
                                >
                                    <ArrowLeft size={18} />
                                </button>
                                <h1 className="text-[27px] font-extrabold tracking-[-0.025em] text-white truncate">{t('auxDashboardTitle', 'Usage Dashboard')}</h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                {/* Period presets */}
                                <div className="flex items-center gap-1 p-1 rounded-ctl border border-pai-subtle bg-white/[0.03]">
                                    {PRESETS.map(p => (
                                        <button
                                            key={p.days}
                                            onClick={() => applyPreset(p.days)}
                                            className={`${segClass(activePreset === p.days)} tnum`}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => setShowCustomRange(prev => !prev)}
                                        className={`${segClass(showCustomRange || !activePreset)} flex items-center gap-1`}
                                        title={t('auxCustomDateRangeTitle', 'Custom date range')}
                                    >
                                        <Calendar size={12} />
                                        {t('auxCustomRange', 'Custom')}
                                    </button>
                                </div>
                                {/* Billing Cycle toggle */}
                                <button
                                    onClick={toggleBillingCycle}
                                    aria-pressed={billingCycle}
                                    className={`flex items-center gap-1.5 h-[38px] px-3 text-[13px] font-semibold rounded-ctl border transition-colors ${
                                        billingCycle
                                            ? 'border-pai-amber/40 bg-pai-amber/[0.08] text-pai-amber'
                                            : 'border-pai-subtle text-pai-meta hover:text-white hover:bg-white/[0.06]'
                                    }`}
                                    title={t('auxBillingPTTitle', 'Align date boundaries to Google Cloud billing cycle (midnight Pacific Time)')}
                                >
                                    <Clock size={13} />
                                    {t('auxBillingPT', 'Billing (PT)')}
                                </button>
                                <button
                                    onClick={loadData}
                                    disabled={loading}
                                    aria-label={t('auxRefresh', 'Refresh')}
                                    className="pai-icon-btn w-9 h-9 disabled:opacity-40"
                                >
                                    <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                                </button>
                            </div>
                        </div>

                        {/* Custom date range row */}
                        {showCustomRange && (
                            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-pai-hairline">
                                <span className="text-[13px] text-pai-meta">{t('auxFrom', 'From')}</span>
                                <input
                                    type="date"
                                    value={customStart}
                                    onChange={(e) => setCustomStart(e.target.value)}
                                    className="pai-input px-2.5 py-1.5 text-[13px] tnum [color-scheme:dark]"
                                />
                                <span className="text-[13px] text-pai-meta">{t('auxTo', 'to')}</span>
                                <input
                                    type="date"
                                    value={customEnd}
                                    onChange={(e) => setCustomEnd(e.target.value)}
                                    className="pai-input px-2.5 py-1.5 text-[13px] tnum [color-scheme:dark]"
                                />
                                <button
                                    onClick={applyCustomRange}
                                    disabled={!customStart || !customEnd}
                                    className="pai-btn-primary h-9 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {t('auxApply', 'Apply')}
                                </button>
                            </div>
                        )}

                        {/* Tabs */}
                        <div className="flex gap-1 mt-4">
                            {tabs.map(tabItem => (
                                <button
                                    key={tabItem.id}
                                    onClick={() => setTab(tabItem.id)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-[13.5px] font-semibold rounded-seg border transition-colors ${
                                        tab === tabItem.id
                                            ? 'bg-white/[0.1] text-white border-pai'
                                            : 'border-transparent text-pai-meta hover:text-white hover:bg-white/[0.06]'
                                    }`}
                                >
                                    <tabItem.icon size={14} />
                                    {tabItem.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-4 md:px-7 py-5">
                        {/* Context indicator */}
                        <div className="mb-5 flex flex-wrap items-center gap-2 text-[13px] text-pai-meta">
                            <span>{t('auxShowingDataFor', 'Showing data for:')}</span>
                            <span className="font-semibold text-white border border-pai-strong px-2 py-0.5 rounded-full text-[12.5px]">
                                {t('auxAllUsers', 'All Users')}
                            </span>
                            <span>&middot;</span>
                            <span className="tnum">
                                {activePreset
                                    ? (activePreset === 1 ? t('auxLast24Hours', 'Last 24 hours') : t('auxLastNDays', 'Last {n} days', { n: activePreset }))
                                    : t('auxDateRangeTo', '{start} to {end}', { start: startDate, end: endDate })}
                            </span>
                            {billingCycle && (
                                <span className="text-pai-amber font-semibold border border-pai-amber/35 bg-pai-amber/[0.08] px-2 py-0.5 rounded-full text-[12px]">
                                    {t('auxBillingCyclePT', 'Billing Cycle (PT)')}
                                </span>
                            )}
                        </div>

                        {error && (
                            <div className="mb-5 px-3.5 py-2.5 rounded-row border border-pai-red/35 bg-pai-red/[0.06] text-pai-red text-[13.5px]">
                                {error}
                            </div>
                        )}

                        {loading && !summary ? (
                            <div className="flex items-center justify-center py-20">
                                <div className="animate-spin h-8 w-8 border-[3px] border-white/70 border-t-transparent rounded-full" />
                            </div>
                        ) : (
                            <>
                                {tab === 'overview' && summary && <OverviewTab summary={summary} />}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const OverviewTab = ({ summary }) => {
    const { t } = useLanguage();
    return (
    <div>
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <StatCard icon={Zap} label={t('auxTotalCalls', 'Total Calls')} value={summary.total_calls.toLocaleString()} color="text-white/70" />
            <StatCard icon={Database} label={t('auxInputTokens', 'Input Tokens')} value={formatTokens(summary.total_prompt_tokens)} sub={t('auxCachedSuffix', '{n} cached', { n: formatTokens(summary.total_cached_tokens) })} color="text-pai-blue" />
            <StatCard icon={Database} label={t('auxOutputTokens', 'Output Tokens')} value={formatTokens(summary.total_completion_tokens)} color="text-pai-amber" />
            <StatCard icon={Percent} label={t('auxCacheHitRate', 'Cache Hit Rate')} value={formatPct(cacheHitPct(summary.cache_hit_pct, summary.total_cached_tokens, summary.total_prompt_tokens))} sub={t('auxCacheHitSub', 'cached input bills at 1/10th')} color="text-pai-green" />
            <StatCard icon={DollarSign} label={t('auxEstCost', 'Est. Cost')} value={formatCost(summary.estimated_cost_usd)} sub={t('auxDaysSub', '{n} days', { n: summary.period_days })} color="text-white/70" />
        </div>

        {/* #416 — the figures above stop dropping when an account is deleted,
            because the backend carries that account's ANONYMOUS totals forward
            into usage_aggregates and adds them back here. Say so: a number that
            silently includes people who no longer exist is worse than one that
            says it does. Rendered only when such totals actually contributed,
            so it disappears on a range with none (and when the aggregates table
            is unreachable, which reports false). */}
        {summary.includes_deleted_accounts && (
            <div
                data-testid="usage-includes-deleted"
                className="mb-6 flex items-start gap-2 rounded-row border border-pai-subtle bg-white/[0.03] px-3.5 py-2.5 text-[12.5px] text-pai-meta"
            >
                <Info size={14} className="mt-0.5 flex-shrink-0 text-pai-blue" />
                <span>{t('auxIncludesDeletedAccounts', 'Includes anonymised totals carried over from deleted accounts. Those figures cannot be attributed to any user and are excluded from per-user views.')}</span>
            </div>
        )}

        {/* #334 — anonymous module telemetry (aggregate-only, small groups withheld). */}
        <TelemetrySection telemetry={summary.telemetry} daily={summary.daily} energy={summary.energy} />

        {/* Grounding */}
        {summary.grounding && (summary.grounding.calls_with_grounding_enabled > 0 || summary.grounding.calls_where_grounding_used > 0) && (
            <section className="pai-card px-5 py-4 mb-6">
                <h3 className={CARD_TITLE}>
                    <Globe size={16} className="text-pai-meta" />
                    {t('auxGoogleSearchGrounding', 'Google Search Grounding')}
                </h3>
                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <div className="pai-eyebrow">{t('auxGroundingEnabled', 'Enabled')}</div>
                        <div className="mt-1 text-[22px] font-extrabold text-white tnum" translate="no">{summary.grounding.calls_with_grounding_enabled}</div>
                    </div>
                    <div>
                        <div className="pai-eyebrow">{t('auxGroundingActuallyUsed', 'Actually Used')}</div>
                        <div className="mt-1 text-[22px] font-extrabold text-white tnum" translate="no">{summary.grounding.calls_where_grounding_used}</div>
                    </div>
                    <div>
                        <div className="pai-eyebrow">{t('auxGroundingSearchQueries', 'Search Queries')}</div>
                        <div className="mt-1 text-[22px] font-extrabold text-white tnum" translate="no">{summary.grounding.total_search_queries}</div>
                    </div>
                </div>
            </section>
        )}

        {/* By pipeline step */}
        <section className="pai-card px-5 py-4 mb-6">
            <h3 className={CARD_TITLE}>{t('auxTokenUsageByStep', 'Token Usage by Pipeline Step')}</h3>
            <div className="space-y-2">
                {Object.entries(summary.by_step || {})
                    .sort((a, b) => (b[1].prompt_tokens || 0) - (a[1].prompt_tokens || 0))
                    .map(([step, data]) => {
                        const maxTokens = Math.max(...Object.values(summary.by_step).map(d => d.prompt_tokens || 0));
                        const pct = maxTokens > 0 ? ((data.prompt_tokens || 0) / maxTokens) * 100 : 0;
                        return (
                            <div key={step} className="flex items-center gap-3 min-w-0">
                                <div className="w-36 text-[13px] font-semibold text-pai-body truncate" title={step}>
                                    {STEP_LABELS[step] || step}
                                </div>
                                <div className="flex-1 min-w-0 h-7 bg-white/[0.04] rounded-seg overflow-hidden relative">
                                    <div
                                        className="h-full bg-pai-blue/[0.12] border-r border-pai-blue/50 transition-all"
                                        style={{ width: `${pct}%` }}
                                    />
                                    <span className="absolute inset-0 flex items-center px-2.5 text-[12px] font-medium text-pai-body tnum truncate" translate="no">
                                        {t('auxInOutCalls', '{in} in / {out} out ({n} calls)', { in: formatTokens(data.prompt_tokens), out: formatTokens(data.completion_tokens), n: data.count })}
                                    </span>
                                </div>
                                <div className="w-24 text-right text-[12px] text-pai-meta tnum shrink-0" title={t('auxCacheHitRateTitle', 'Prompt-cache hit rate (cached ÷ input tokens)')}>
                                    {t('auxCacheSuffix', '{pct} cache', { pct: formatPct(cacheHitPct(data.cache_hit_pct, data.cached_tokens, data.prompt_tokens)) })}
                                </div>
                            </div>
                        );
                    })}
            </div>
        </section>

        {/* Daily breakdown */}
        {summary.daily && Object.keys(summary.daily).length > 0 && (
            <section className="pai-card px-5 py-4">
                <h3 className={CARD_TITLE}>{t('auxDailyUsage', 'Daily Usage')}</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-[13.5px]">
                        <thead>
                            <tr className="border-b border-pai-subtle">
                                <th className={`${TH} text-left pr-4`}>{t('auxColDate', 'Date')}</th>
                                <th className={`${TH} text-right px-3`}>{t('auxColCalls', 'Calls')}</th>
                                <th className={`${TH} text-right px-3`}>{t('auxColInput', 'Input')}</th>
                                <th className={`${TH} text-right px-3`}>{t('auxColOutput', 'Output')}</th>
                                <th className={`${TH} text-right px-3`}>{t('auxColCached', 'Cached')}</th>
                                <th className={`${TH} text-right px-3`}>{t('auxColCachePct', 'Cache %')}</th>
                                <th className={`${TH} text-right pl-3`}>{t('auxColTotal', 'Total')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(summary.daily)
                                .sort((a, b) => b[0].localeCompare(a[0]))
                                .map(([day, data]) => (
                                    <tr key={day} className="border-b border-pai-hairline last:border-b-0 text-pai-secondary tnum" translate="no">
                                        <td className="py-2.5 pr-4 font-semibold text-pai-body">{day}</td>
                                        <td className="text-right py-2.5 px-3">{data.calls}</td>
                                        <td className="text-right py-2.5 px-3">{formatTokens(data.prompt_tokens)}</td>
                                        <td className="text-right py-2.5 px-3">{formatTokens(data.completion_tokens)}</td>
                                        <td className="text-right py-2.5 px-3">{formatTokens(data.cached_tokens)}</td>
                                        <td className="text-right py-2.5 px-3">{formatPct(cacheHitPct(data.cache_hit_pct, data.cached_tokens, data.prompt_tokens))}</td>
                                        <td className="text-right py-2.5 pl-3 font-semibold text-white">{formatTokens(data.total_tokens)}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </section>
        )}
    </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// The Conversations + Documents tabs were DELETED (monitoring lockdown,
// 2026-10-01) along with TurnCallRow / TruncatedText / ConversationsTab /
// DocumentsTab. They rendered conversation titles, per-turn user_query and
// assistant_response, raw llm_input/llm_output panes and document filenames —
// personal data a credit-usage dashboard has no need of. The BE endpoints
// behind them are gone too, so nothing here can be revived without also
// reintroducing a route. ⚠️ Don't.
// ─────────────────────────────────────────────────────────────────────────────
// The Users tab was DELETED (#470, 2026-10-16) along with PlanCell /
// EnergyPointsCell / UsersTab: the dashboard is aggregate-only, and the BE
// routes behind it (GET /api/usage/users, POST /api/usage/users/<id>/energy/adjust)
// are gone. Energy grants are the local script BE/scripts/adjust_energy.py.
// ─────────────────────────────────────────────────────────────────────────────

export default DashboardPage;
